import {useInterceptor} from '@jobseeker/common-core';
import {
  saveJob,
  unFollowJob,
  unFollowRecruiter,
} from '@jobseeker/common-core/reducers/job/thunkServices/jobfeed';
import {AppState, Job} from '@jobseeker/common-core/types';
import {additionalInfoIconMap} from '@jobseeker/feature-job/components/JobFeed/V2';
import {InfiniteScroll, JobList, Loader} from '@jobseeker/lotus/components';
import {
  CheckBox,
  CityPageLisiting,
  HiristSeoLinking,
  Tooltip,
} from '@jobseeker/lotus/elements';
import {VersionVariants} from '@jobseeker/lotus/enums';
import {useIsLargeDevice} from '@jobseeker/lotus/hooks';
import {COMPANY, LOCATION_BASED} from '@jobseeker/common-core/constants';
import {Box, Card, Typography} from '@mui/material';
import kebabCase from 'lodash/kebabCase';
import dayjs from 'dayjs';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {useJobVisibilityTracker} from '@jobseeker/feature-job/hooks';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import {MULTI_APPLY_MAX_JOBS} from '@jobseeker/feature-job/components/constants';
type companyJobProps = {
  jobs: Job[];
  hasMore: boolean;
};
type CompanyFeedProps = {
  onLoadMore: () => any;
  companyJobs: companyJobProps;
  setCompanyJobList?: Dispatch<SetStateAction<companyJobProps>>;
  title: string;
  checkJobs?: Map<string, string | undefined>;
  setCheckJobs?: Dispatch<SetStateAction<Map<string, string | undefined>>>;
  companyRefCode?: string;
};
const CompanyJobFeed = ({
  onLoadMore,
  companyJobs,
  setCompanyJobList,
  title,
  checkJobs,
  setCheckJobs,
  companyRefCode,
}: CompanyFeedProps) => {
  // const jobFeedStore = useSelector((state: AppState) => state.companyJob);
  let learnNGrowAdIndex = -1;
  const dispatch = useDispatch<any>();

  const {isLoggedIn, userId} = useSelector((state: AppState) => state.auth);

  const onJobSave = (jobData: any, unsave: boolean) => {
    if (isLoggedIn) {
      dispatch(
        saveJob({
          ...jobData,
          jobId: jobData.id,
          unsave: unsave,
          userId: userId,
        })
      );
      setCompanyJobList &&
        setCompanyJobList({
          jobs: companyJobs.jobs.map(job =>
            job.id === jobData.id ? {...job, saved: !job.saved} : job
          ) as Job[],
          hasMore: companyJobs.hasMore,
        });
    }
  };
  const isLarge = useIsLargeDevice();
  const {device} = useSelector(
    (state: AppState) => state.app.layout,
    shallowEqual
  );

  const onJobActionClick = (jobId: string, action: string) => {
    if (action == 'JOB') {
      dispatch(
        unFollowJob({
          undo: false,
          jobId: jobId,
          userId: userId,
        })
      );
    } else {
      dispatch(
        unFollowRecruiter({
          undo: false,
          recruiterId: 'NO_REC_ID',
          userId: userId,
        })
      );
    }
  };
  const [checkedJobs, setCheckedJobs] = useState<
    Map<string, string | undefined>
  >(() => new Map());

  const addItem = (jobId: string, refCode?: string) => {
    setCheckedJobs(prev => {
      if (!prev.has(jobId) && prev.size >= MULTI_APPLY_MAX_JOBS) {
        return prev;
      }

      return new Map(prev).set(jobId, refCode);
    });
  };

  const removeItem = (jobId: string) => {
    setCheckedJobs(prev => {
      const next = new Map(prev);
      next.delete(jobId);
      return next;
    });
  };

  const onCheckClick = (jobId: string, refCode?: string) => {
    if (checkedJobs.has(jobId)) {
      removeItem(jobId);
    } else {
      addItem(jobId, refCode);
    }
  };
  const onUndoActionClick = (jobId: string, action: string) => {
    if (action == 'JOB') {
      dispatch(
        unFollowJob({
          undo: true,
          jobId: jobId,
          userId: userId,
        })
      );
    } else {
      dispatch(
        unFollowRecruiter({
          undo: true,
          recruiterId: 'NO_REC_ID',
          userId: userId,
        })
      );
    }
  };
  useEffect(() => {
    if (setCheckJobs) {
      setCheckJobs(new Map(checkedJobs));
    }
  }, [checkedJobs]);
  const page = useSelector((state: AppState) => state.app.page, shallowEqual);

  const onJobClick = (job: Job, index: number) => {
    const slug = kebabCase(job.title.replace(/&/g, '-and-'));
  };
  const isHirist = process.env.NEXT_PUBLIC_APP_NAME === 'hirist';

  // Hook to track job visibility in viewport and fire tracking events
  const {registerJobElement} = useJobVisibilityTracker({
    batchSize: 5,
  });

  const {
    jobfeed: {jobInfoKeyElements},
  } = useInterceptor();
  const WrapperCard: any = isHirist ? Box : Card;
  const isMultiApplyLimitReached = checkedJobs.size >= MULTI_APPLY_MAX_JOBS;
  const multiApplyLimitText = `You can select up to ${MULTI_APPLY_MAX_JOBS} jobs at a time`;
  const [multiApplyTooltipOpenFor, setMultiApplyTooltipOpenFor] = useState<
    string | null
  >(null);
  const multiApplyTooltipTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);

  const closeMultiApplyLimitTooltip = useCallback(() => {
    if (multiApplyTooltipTimeoutRef.current) {
      clearTimeout(multiApplyTooltipTimeoutRef.current);
      multiApplyTooltipTimeoutRef.current = null;
    }
    setMultiApplyTooltipOpenFor(null);
  }, []);

  const showMultiApplyLimitTooltip = useCallback(
    (jobId: string) => {
      if (isLarge) {
        return;
      }

      if (multiApplyTooltipTimeoutRef.current) {
        clearTimeout(multiApplyTooltipTimeoutRef.current);
      }

      setMultiApplyTooltipOpenFor(jobId);
      multiApplyTooltipTimeoutRef.current = setTimeout(() => {
        setMultiApplyTooltipOpenFor(null);
        multiApplyTooltipTimeoutRef.current = null;
      }, 1500);
    },
    [isLarge]
  );

  useEffect(
    () => () => {
      if (multiApplyTooltipTimeoutRef.current) {
        clearTimeout(multiApplyTooltipTimeoutRef.current);
      }
    },
    []
  );

  useEffect(() => {
    if (!isMultiApplyLimitReached) {
      closeMultiApplyLimitTooltip();
    }
  }, [closeMultiApplyLimitTooltip, isMultiApplyLimitReached]);

  return (
    <WrapperCard
      sx={
        isHirist
          ? {mr: 0}
          : {
              boxShadow: 2,
              borderRadius: 4,
            }
      }
    >
      <Box>
        <Typography
          component={!isHirist && page === LOCATION_BASED ? 'h2' : 'h1'}
          marginTop={{xxs: 2, md: 3}}
          marginLeft={isHirist ? 0 : 2}
          marginBottom={1}
          color="var(--Font-Color-Font-color, #333)"
          fontFamily={isHirist ? 'Nunito Sans' : 'Poppins'}
          fontSize={
            isHirist
              ? {xxs: '18px', md: 20}
              : page === LOCATION_BASED
              ? '20px'
              : '16px'
          }
          fontStyle="normal"
          fontWeight={!isHirist && page === LOCATION_BASED ? '500' : '600'}
          lineHeight={!isHirist && page === LOCATION_BASED ? 1.5 : '22px'}
        >
          {title}
        </Typography>
        <InfiniteScroll
          //key={`${Date.now()}`}
          dataLength={companyJobs?.jobs?.length}
          next={onLoadMore}
          hasMore={!!companyJobs.hasMore}
          onScroll={undefined}
          loader={
            <Box
              sx={{
                display: 'flex',
                textAlign: 'center',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Loader />
            </Box>
          }
          style={{
            scrollbarWidth: 'none',
          }}
        >
          {companyJobs?.jobs?.map((job: Job | any, index: number) => {
            const additionalInfo: any[] = [];
            jobInfoKeyElements.forEach(
              (key: {value: string; label: string}) => {
                const {label, value} = key;
                if (job[label]) {
                  additionalInfo.push({
                    label: value,
                    // value,
                    icon: additionalInfoIconMap[label],
                  });
                }
              }
            );

            return (
              <>
                <Box
                  key={`${job.id}_${index}`}
                  mb={isHirist ? 2 : 0}
                  alignItems={'center'}
                  mt={job?.applied && index == 0 ? 1 : 0}
                >
                  <Box display={'flex'} height={'fit-content'}>
                    {isHirist && (
                      <Box
                        component={
                          job.mediaResume || job?.assessmentFlags
                            ? Tooltip
                            : Box
                        }
                        display={
                          job.mediaResume || job?.assessmentFlags
                            ? 'unset'
                            : 'flex'
                        }
                        arrow
                        title={
                          job.mediaResume || job?.assessmentFlags
                            ? 'The recruiter has some questions before you apply'
                            : ''
                        }
                        height={'fit-content'}
                      >
                        {(() => {
                          const isChecked = checkedJobs.has(job.id.toString());
                          const isBaseDisabled =
                            job.mediaResume ||
                            job?.assessmentFlags ||
                            job?.applied;
                          const isDisabledByMultiApplyLimit =
                            !isChecked &&
                            !isBaseDisabled &&
                            isMultiApplyLimitReached;
                          const isMobileMultiApplyTooltipOpen =
                            !isLarge &&
                            isDisabledByMultiApplyLimit &&
                            multiApplyTooltipOpenFor === job.id.toString();

                          return (
                            <Box
                              // ml={{xxs: 2, md: 4, lg: 6}}
                              mx={2}
                              ml={{xxs: page == LOCATION_BASED ? 0 : 2, md: 2}}
                              mt={3.25}
                              display={'flex'}
                              height={'fit-content'}
                              data-testid={`company-job-checkbox-container-${job.id}`}
                            >
                              <Tooltip
                                PopperProps={{
                                  sx: {
                                    zIndex: 8,
                                  },
                                }}
                                title={
                                  isDisabledByMultiApplyLimit
                                    ? multiApplyLimitText
                                    : ''
                                }
                                disableHoverListener={
                                  !isDisabledByMultiApplyLimit || !isLarge
                                }
                                disableFocusListener={
                                  !isDisabledByMultiApplyLimit || !isLarge
                                }
                                disableTouchListener
                                onClose={closeMultiApplyLimitTooltip}
                                open={
                                  isLarge
                                    ? undefined
                                    : isMobileMultiApplyTooltipOpen
                                }
                              >
                                <Box
                                  component="span"
                                  sx={{
                                    display: 'inline-flex',
                                    cursor: isDisabledByMultiApplyLimit
                                      ? 'not-allowed'
                                      : 'pointer',
                                  }}
                                  onClickCapture={event => {
                                    if (!isDisabledByMultiApplyLimit) {
                                      return;
                                    }

                                    event.preventDefault();
                                    event.stopPropagation();
                                    showMultiApplyLimitTooltip(
                                      job.id.toString()
                                    );
                                  }}
                                  onTouchEndCapture={event => {
                                    if (!isDisabledByMultiApplyLimit) {
                                      return;
                                    }

                                    event.preventDefault();
                                    event.stopPropagation();
                                    showMultiApplyLimitTooltip(
                                      job.id.toString()
                                    );
                                  }}
                                >
                                  <CheckBox
                                    variant={VersionVariants.VERSION_HIRIST}
                                    sx={{
                                      mx: 0,
                                      cursor: isDisabledByMultiApplyLimit
                                        ? 'not-allowed'
                                        : 'pointer',
                                    }}
                                    checked={isChecked}
                                    disabled={isBaseDisabled}
                                    value={job.id.toString()}
                                    onChange={e => {
                                      onCheckClick(
                                        e.target.value,
                                        job.refCode
                                          ? job.refCode.toString()
                                          : companyRefCode || 'clp'
                                      );
                                    }}
                                    data-testid={`company-job-checkbox-${job.id}`}
                                  />
                                </Box>
                              </Tooltip>
                            </Box>
                          );
                        })()}
                      </Box>
                    )}
                    <Box
                      key={`${job.id}_${index}`}
                      width={'100%'}
                      ref={(el: Element | null) =>
                        registerJobElement(
                          el,
                          job.id?.toString(),
                          job.refCode
                            ? job.refCode
                            : page == COMPANY
                            ? 'clp'
                            : '',
                          index + 1
                        )
                      }
                    >
                      <JobList
                        data-testid={`company_jobs_${job.id}`}
                        noShadow
                        // key={`${index}${Date.now()}`}
                        title={job?.title
                          ?.split('(')[0]
                          // ?.replace(/\//gm, '/ ')
                          .trim()}
                        refCode={
                          job.refCode
                            ? job.refCode
                            : page == COMPANY
                            ? 'clp'
                            : ''
                        }
                        jobPos={index + 1}
                        experiance={`${job.min} - ${job.max} yrs`}
                        companyName={job.companyData.companyName}
                        date={dayjs(job.createdTime).toDate()}
                        showApplied={isHirist ? true : true}
                        location={
                          job?.location.length > 3
                            ? 'Multiple Locations'
                            : job?.location?.map((l: any) => l.name).join('/')
                                .length > 30
                            ? 'Multiple Locations'
                            : job?.location?.map((l: any) => l.name).join('/')
                        }
                        variant={
                          process.env.NEXT_PUBLIC_APP_NAME === 'hirist'
                            ? VersionVariants.VERSION_HIRIST
                            : VersionVariants.VERSION_TWO
                        }
                        isCompanyJob={Math.random() < 0.5}
                        jobData={job}
                        onJobClick={() => onJobClick(job, index)}
                        additionalInfo={additionalInfo}
                        isPremiumJob={!!job.premium}
                        isSaved={!!job.saved}
                        onSaveClick={onJobSave}
                        onJobActionClick={onJobActionClick}
                        onUndoActionClick={onUndoActionClick}
                        companyImgUrl={
                          job.recruiter?.centralizedCompanyId &&
                          !job?.confidential
                            ? `${process.env.NEXT_PUBLIC_S3_COMPANY_LOGO_BUCKET}${job.recruiter?.centralizedCompanyId}.webp`
                            : ''
                        }
                        fallbackCompanyUrl={
                          job.recruiter?.centralizedCompanyId &&
                          !job?.confidential
                            ? `${process.env.NEXT_PUBLIC_S3_COMPANY_LOGO_BUCKET}${job.recruiter?.centralizedCompanyId}.webp`
                            : ''
                        }
                      />
                    </Box>
                  </Box>
                </Box>
                {isHirist && !(device == 'mobile') && page != LOCATION_BASED ? (
                  <>
                    {index == 1 && (
                      <Box mb={'16px'}>
                        <HiristSeoLinking source={'company'} />
                      </Box>
                    )}
                    {index == 5 && (
                      <Box mb={2}>
                        <CityPageLisiting type="company" />
                      </Box>
                    )}
                    {[12].includes(index) && (
                      <Box mb={2}>
                        <CityPageLisiting type="city" />
                      </Box>
                    )}
                  </>
                ) : (
                  <></>
                )}
              </>
            );
          })}
        </InfiniteScroll>
      </Box>
    </WrapperCard>
  );
};

export default CompanyJobFeed;
